import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, User, ChevronRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import ShareButtons from "./ShareButtons";
import Navbar from "./Navbar";
import Footer from "./Footer";

function ReadingProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-0.5 bg-transparent">
      <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] transition-[width] duration-75" style={{ width: `${progress}%` }} />
    </div>
  );
}

function RelatedArticles({ articles, currentSlug, backLabel }) {
  const related = articles.filter((a) => a.slug !== currentSlug).slice(0, 2);
  if (related.length === 0) return null;
  return (
    <div className="mt-16 pt-10 border-t border-slate-800">
      <h3 className="text-slate-300 text-[10px] uppercase tracking-widest mb-8">Related Articles</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {related.map((a) => (
          <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`related-${a.slug}`}
            className="group block bg-[#162A45] border border-slate-800 hover:border-[#D4AF37]/40 p-6 transition-all duration-300">
            <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest">{a.category}</span>
            <h4 className="text-white text-sm font-semibold mt-2 mb-3 group-hover:text-[#D4AF37] transition-colors leading-snug"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{a.title}</h4>
            <span className="text-slate-500 text-xs flex items-center gap-1">
              {a.readTime} <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function BlogArticle() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const b = t.blog;
  const article = b.articles.find((a) => a.slug === slug);
  const articleUrl = article ? `https://www.advaltis.com/blog/${article.slug}` : "";

  useEffect(() => {
    if (!article) return;
    document.title = `${article.title} | Advaltis Consulting`;
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[property="og:title"]', "content", article.title);
    setMeta('meta[property="og:description"]', "content", article.excerpt);
    setMeta('meta[property="og:image"]', "content", article.image);
    setMeta('meta[property="og:url"]', "content", articleUrl);
    setMeta('meta[name="description"]', "content", article.excerpt);
    return () => { document.title = "Advaltis Consulting"; };
  }, [article, articleUrl]);

  if (!article) {
    return (
      <main className="bg-[#0F1A2E] min-h-screen">
        <Navbar />
        <div className="pt-32 pb-24 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-white text-2xl mb-4">Article not found</h1>
          <Link to="/blog" className="text-[#D4AF37] hover:underline">Back to Blog</Link>
        </div>
        <Footer />
      </main>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: { "@type": "Person", name: article.author },
    publisher: { "@type": "Organization", name: "Advaltis Consulting", url: "https://www.advaltis.com" },
    datePublished: article.date,
    url: articleUrl,
  };

  return (
    <main className="bg-[#0F1A2E] min-h-screen">
      <ReadingProgressBar />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <article className="pt-32 pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 mb-8">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            <ChevronRight size={10} />
            <Link to="/blog" className="hover:text-[#D4AF37] transition-colors">Blog</Link>
            <ChevronRight size={10} />
            <span className="text-slate-400 truncate max-w-[200px] md:max-w-none">{article.title}</span>
          </nav>

          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-slate-400 hover:text-[#D4AF37] text-sm mb-10 transition-colors duration-300">
            <ArrowLeft size={14} /> {b.backLabel}
          </Link>

          <div className="mb-8">
            <img src="/advaltis-logo-transparent.png" alt="Advaltis" className="h-10 mb-6 opacity-80" />
            <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-medium">{article.category}</span>
            <h1 data-testid="article-title" className="text-3xl md:text-4xl text-white leading-tight mt-3 mb-6"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-slate-400 text-xs">
              <div className="flex items-center gap-1.5"><User size={12} />{article.author}</div>
              <div className="flex items-center gap-1.5"><Calendar size={12} />{article.date}</div>
              <div className="flex items-center gap-1.5"><Clock size={12} />{article.readTime}</div>
            </div>
          </div>

          <div className="h-64 md:h-80 overflow-hidden mb-12">
            <img src={article.image} alt={article.title} loading="lazy" className="w-full h-full object-cover" />
            <div className="relative -mt-20 h-20 bg-gradient-to-t from-[#0F1A2E] to-transparent" />
          </div>

          <div className="prose-advaltis space-y-6">
            {article.body.map((block, i) => {
              if (block.type === "p") return <p key={i} className="text-slate-300 text-base leading-relaxed">{block.text}</p>;
              if (block.type === "h2") return <h2 key={i} className="text-white text-xl font-semibold mt-10 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{block.text}</h2>;
              if (block.type === "ul") return (
                <ul key={i} className="space-y-2 ml-2">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              );
              if (block.type === "quote") return (
                <blockquote key={i} className="border-l-2 border-[#D4AF37] pl-6 py-2 my-8">
                  <p className="text-slate-300 text-base italic leading-relaxed">{block.text}</p>
                </blockquote>
              );
              return null;
            })}
          </div>

          <div className="mt-16 pt-10 border-t border-slate-800 flex items-center justify-between">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-widest hover:gap-3 transition-all duration-300">
              <ArrowLeft size={14} /> {b.backLabel}
            </Link>
            <ShareButtons title={article.title} url={articleUrl} />
          </div>

          <RelatedArticles articles={b.articles} currentSlug={slug} backLabel={b.backLabel} />
        </div>
      </article>
      <Footer />
    </main>
  );
}
